import * as React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useApi, githubAuthApiRef, googleAuthApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 1200,
    margin: '0 auto',
  },
  card: {
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  providerCard: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  avatarSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 80,
  },
  avatar: {
    width: 56,
    height: 56,
    marginBottom: theme.spacing(1),
  },
  providerName: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  contentSection: {
    flex: 1,
    minWidth: 0,
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  userName: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  userEmail: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  description: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  actionSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  button: {
    minWidth: 120,
    textTransform: 'none',
    fontWeight: 500,
  },
  signInButton: {
    minWidth: 120,
    textTransform: 'none',
    fontWeight: 500,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  signOutButton: {
    minWidth: 120,
    textTransform: 'none',
    fontWeight: 500,
    borderColor: theme.palette.divider,
  },
  connectedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.success.main,
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  checkIcon: {
    fontSize: '1.25rem',
  },
}));

interface ProviderStatus {
  connected: boolean;
  profile?: {
    displayName?: string;
    email?: string;
    picture?: string;
  };
}

export const IntegrationsTab = () => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const googleAuth = useApi(googleAuthApiRef);
  
  const [githubStatus, setGithubStatus] = React.useState<ProviderStatus>({ connected: false });
  const [googleStatus, setGoogleStatus] = React.useState<ProviderStatus>({ connected: false });
  const [loading] = React.useState(false);

  // Check existing auth sessions without prompting
  React.useEffect(() => {
    const checkExistingSessions = async () => {
      // Check GitHub session
      try {
        // Try to get access token with optional flag to avoid prompt
        const githubToken = await githubAuth.getAccessToken(['user'], { optional: true });
        if (githubToken) {
          // If we have a token, get the profile
          const githubProfile = await githubAuth.getProfile();
          setGithubStatus({ connected: true, profile: githubProfile });
        }
      } catch (error) {
        // No active GitHub session
        console.debug('No active GitHub session');
      }

      // Check Google session
      try {
        // Try to get access token with optional flag to avoid prompt
        const googleToken = await googleAuth.getAccessToken(['openid', 'profile', 'email'], { optional: true });
        if (googleToken) {
          // If we have a token, get the profile
          const googleProfile = await googleAuth.getProfile();
          setGoogleStatus({ connected: true, profile: googleProfile });
        }
      } catch (error) {
        // No active Google session
        console.debug('No active Google session');
      }
    };

    checkExistingSessions();
  }, [githubAuth, googleAuth]);

  const handleGithubSignIn = async () => {
    try {
      await githubAuth.getAccessToken(['user']);
      // After successful sign in, get the profile
      const profile = await githubAuth.getProfile();
      setGithubStatus({ connected: true, profile });
    } catch (error) {
      console.error('GitHub sign in failed:', error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await googleAuth.getAccessToken(['openid', 'profile', 'email']);
      // After successful sign in, get the profile
      const profile = await googleAuth.getProfile();
      setGoogleStatus({ connected: true, profile });
    } catch (error) {
      console.error('Google sign in failed:', error);
    }
  };

  const handleGithubSignOut = async () => {
    try {
      await githubAuth.signOut();
      setGithubStatus({ connected: false });
    } catch (error) {
      console.error('GitHub sign out failed:', error);
    }
  };

  const handleGoogleSignOut = async () => {
    try {
      await googleAuth.signOut();
      setGoogleStatus({ connected: false });
    } catch (error) {
      console.error('Google sign out failed:', error);
    }
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h5" className={classes.sectionTitle}>
        Available Providers
      </Typography>

      <Card className={classes.card}>
        <CardContent className={classes.providerCard}>
          <Box className={classes.avatarSection}>
            <Avatar
              src={googleStatus.profile?.picture}
              className={classes.avatar}
            >
              G
            </Avatar>
            <Typography className={classes.providerName}>Google</Typography>
          </Box>

          <Box className={classes.contentSection}>
            {googleStatus.connected ? (
              <Box className={classes.userInfo}>
                <Typography className={classes.userName}>
                  {googleStatus.profile?.displayName || 'User'}
                </Typography>
                <Typography className={classes.userEmail}>
                  {googleStatus.profile?.email || ''}
                </Typography>
                <Typography className={classes.description}>
                  Provides authentication towards Google APIs and identities
                </Typography>
              </Box>
            ) : (
              <Typography className={classes.description}>
                Provides authentication towards Google APIs and identities
              </Typography>
            )}
          </Box>

          <Box className={classes.actionSection}>
            {googleStatus.connected ? (
              <>
                <Box className={classes.connectedBadge}>
                  <CheckCircleIcon className={classes.checkIcon} />
                  Connected
                </Box>
                <Button
                  variant="outlined"
                  className={classes.signOutButton}
                  onClick={handleGoogleSignOut}
                >
                  Sign out
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                className={classes.signInButton}
                onClick={handleGoogleSignIn}
              >
                Sign in
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardContent className={classes.providerCard}>
          <Box className={classes.avatarSection}>
            <Avatar
              src={githubStatus.profile?.picture}
              className={classes.avatar}
            >
              GH
            </Avatar>
            <Typography className={classes.providerName}>GitHub</Typography>
          </Box>

          <Box className={classes.contentSection}>
            {githubStatus.connected ? (
              <Box className={classes.userInfo}>
                <Typography className={classes.userName}>
                  {githubStatus.profile?.displayName || 'User'}
                </Typography>
                <Typography className={classes.userEmail}>
                  {githubStatus.profile?.email || ''}
                </Typography>
                <Typography className={classes.description}>
                  Provides authentication towards GitHub APIs and identities
                </Typography>
              </Box>
            ) : (
              <Typography className={classes.description}>
                Provides authentication towards GitHub APIs and identities
              </Typography>
            )}
          </Box>

          <Box className={classes.actionSection}>
            {githubStatus.connected ? (
              <>
                <Box className={classes.connectedBadge}>
                  <CheckCircleIcon className={classes.checkIcon} />
                  Connected
                </Box>
                <Button
                  variant="outlined"
                  className={classes.signOutButton}
                  onClick={handleGithubSignOut}
                >
                  Sign out
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                className={classes.signInButton}
                onClick={handleGithubSignIn}
              >
                Sign in
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

